import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu, X, ChevronDown, School, Users, GraduationCap, BookOpen,
  Briefcase, Mail, Info, Wrench, ExternalLink
} from 'lucide-react';
import { cn } from '@/lib/utils';

const products = [
  {
    name: 'School ERP',
    description: 'Complete K-12 Campus Management',
    icon: School,
    href: '/products/school-erp',
    color: 'teal',
  },
  {
    name: 'College ERP',
    description: 'NEP 2020 Compliant Higher Education Suite',
    icon: GraduationCap,
    href: '/products/college-erp',
    color: 'orange',
  },
  {
    name: 'Lynkt',
    description: 'Teacher Empowerment Platform',
    icon: Users,
    href: '/products/lynkt',
    color: 'teal',
  },
  {
    name: 'LMS',
    description: 'AI-Powered Learning Management',
    icon: BookOpen,
    href: '/products/lms',
    color: 'orange',
  },
];

const navLinks = [
  { name: 'Home', href: '/' },
  { name: 'Products', href: '#', hasDropdown: true },
  { name: 'About', href: '/about' },
  { name: 'Teachers', href: '/teachers' },
  { name: 'Careers', href: '/careers' },
  { name: 'Contact', href: '/contact' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const handleDropdownToggle = (linkName) => {
    setActiveDropdown(activeDropdown === linkName ? null : linkName);
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
          isScrolled 
            ? 'bg-void/90 backdrop-blur-xl border-b border-glass-border' 
            : 'bg-transparent'
        )}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <motion.div 
                className="relative w-10 h-10 flex items-center justify-center"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <div className="absolute inset-0 bg-gradient-teal rounded-lg opacity-20 group-hover:opacity-40 transition-opacity" />
                <span className="text-display-xl text-2xl text-teal">T</span>
              </motion.div>
              <div className="hidden sm:block">
                <span className="text-display text-lg text-foreground">Tattva</span>
                <span className="text-display text-lg text-teal ml-1">Techworkz</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => link.hasDropdown && setActiveDropdown(link.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {link.hasDropdown ? (
                    <button
                      className={cn(
                        'flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium',
                        'text-muted-foreground hover:text-foreground transition-colors duration-300',
                        activeDropdown === link.name && 'text-foreground'
                      )}
                    >
                      {link.name}
                      <ChevronDown 
                        className={cn(
                          'w-4 h-4 transition-transform duration-300',
                          activeDropdown === link.name && 'rotate-180'
                        )} 
                      />
                    </button>
                  ) : (
                    <Link
                      to={link.href}
                      className={cn(
                        'px-4 py-2 rounded-lg text-sm font-medium',
                        'text-muted-foreground hover:text-foreground transition-colors duration-300',
                        location.pathname === link.href && 'text-teal'
                      )}
                    >
                      {link.name}
                    </Link>
                  )}

                  {/* Mega Menu Dropdown */}
                  <AnimatePresence>
                    {link.hasDropdown && activeDropdown === link.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: 'auto' }}
                        exit={{ opacity: 0, y: 10, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[500px]"
                      >
                        <div className="glass-card p-6 overflow-hidden">
                          <div className="grid grid-cols-1 gap-4">
                            {products.map((product, index) => (
                              <motion.div
                                key={product.name}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1, duration: 0.3 }}
                              >
                                <Link
                                  to={product.href}
                                  className={cn(
                                    'group flex items-start gap-4 p-4 rounded-xl',
                                    'transition-all duration-300',
                                    'hover:bg-glass-hover'
                                  )}
                                >
                                  <div className={cn(
                                    'w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0',
                                    'transition-all duration-300 group-hover:scale-110',
                                    product.color === 'teal' 
                                      ? 'bg-teal/10 text-teal group-hover:bg-teal/20' 
                                      : 'bg-orange/10 text-orange group-hover:bg-orange/20'
                                  )}>
                                    <product.icon className="w-6 h-6" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-foreground font-display font-semibold">
                                        {product.name}
                                      </span>
                                      <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">
                                      {product.description}
                                    </p>
                                  </div>
                                </Link>
                              </motion.div>
                            ))}
                          </div>
                          
                          {/* Footer Section */}
                          <div className="mt-4 pt-4 border-t border-glass-border">
                            <Link 
                              to="/products"
                              className="flex items-center justify-center gap-2 text-sm text-teal hover:text-teal-light transition-colors"
                            >
                              View All Products
                              <ExternalLink className="w-4 h-4" />
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            {/* CTA Button & Mobile Toggle */}
            <div className="flex items-center gap-4">
              <Link to="/contact" className="hidden sm:block">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary"
                >
                  Book a Demo
                </motion.button>
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-glass-hover transition-colors"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6 text-foreground" />
                ) : (
                  <Menu className="w-6 h-6 text-foreground" />
                )}
              </button>
            </div>
          </div>
        </nav>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-void/95 backdrop-blur-xl"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            <motion.nav
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-void-light border-l border-glass-border p-6 pt-24"
            >
              <div className="flex flex-col gap-2">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                  >
                    {link.hasDropdown ? (
                      <div>
                        <button
                          onClick={() => handleDropdownToggle(link.name)}
                          className="flex items-center justify-between w-full p-4 rounded-xl text-lg font-display font-semibold text-foreground hover:bg-glass-hover transition-colors"
                        >
                          {link.name}
                          <ChevronDown 
                            className={cn(
                              'w-5 h-5 transition-transform duration-300',
                              activeDropdown === link.name && 'rotate-180'
                            )} 
                          />
                        </button>
                        
                        <AnimatePresence>
                          {activeDropdown === link.name && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden pl-4"
                            >
                              {products.map((product) => (
                                <Link
                                  key={product.name}
                                  to={product.href}
                                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-glass-hover transition-colors"
                                >
                                  <product.icon className={cn(
                                    'w-5 h-5',
                                    product.color === 'teal' ? 'text-teal' : 'text-orange'
                                  )} />
                                  <span className="text-foreground">{product.name}</span>
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={link.href}
                        className={cn(
                          'block p-4 rounded-xl text-lg font-display font-semibold',
                          'hover:bg-glass-hover transition-colors',
                          location.pathname === link.href ? 'text-teal' : 'text-foreground'
                        )}
                      >
                        {link.name}
                      </Link>
                    )}
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8"
              >
                <Link to="/contact">
                  <button className="btn-primary w-full">
                    Book a Demo
                  </button>
                </Link>
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
